import React, { useCallback, useEffect, useRef, useState } from "react";
import CustomButton from "src/components/CustomButton";
import { GET_ALL_NOTIFICATIONS } from "src/graphql/query";
import {
  Box,
  Checkbox,
  debounce,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers";
import { getRole, hasPermissionPage } from "src/components/Common/Utils";
import CustomTable from "src/components/CustomTable";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import Loader from "src/components/Loader";
import ErrorComponent from "src/components/ErrorComponent";
import SendBulkMessagesModal from "./SendBulkMessagesModal";
import backButton from "../../Images/backButton.svg";
import { useNavigate } from "react-router";
import { ROUTES } from "src/constant";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';


interface SendBulkMessages {
  _id: string;
}


const ShowNotifications: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50 });
  const [total, setTotal] = useState(0);
  const [list, setList] = useState([]);
  const isSuperAdmin = getRole() === "SuperAdmin";
  const navigate = useNavigate();
  const initialSendMessagesObj = {
    type: "",
    userType: "",
    businessType: "",
    emailBody: "",
    emailSubject: "",
    imageUrl: "",
    businessClaimType: "",
    personalEmailId: "",
    userList: [],
  };
  const [sendMessagesObj, setSendMessagesObj] = useState<any>(initialSendMessagesObj);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(["EMAIL"]);
  const [businessClaimType, setBusinessClaimType] = useState<boolean>(true);
  const [isSendEmail, setIsSendEmail] = useState<boolean | null>(null);
  const [isSendMessage, setIsSendMessage] = useState<boolean | null>(null);


  const COLUMNS = [
    ...(isSuperAdmin
      ? [
        {
          headerName: (
            <Box display={"flex"} alignItems={"center"}>
              <Checkbox
                checked={selectedIndexes.length === list.length && list.length > 0}
                indeterminate={
                  selectedIndexes.length > 0 && selectedIndexes.length < list.length
                }
                onChange={(event) => handleSelectAll(event.target.checked)}
              />
            </Box>
          ),
          field: "select",
          flex: 0.5,
          sortable: false,
          filterable: false,
          disableClickEventBubbling: true,
          renderCell: ({ row }: any) => (
            <Checkbox
              checked={selectedRows.some((item: any) => item._id === row._id)}
              onChange={() => handleRowSelect(row._id)}
            />
          )
        },
      ]
      : []),
    {
      headerName: "Business / User Name",
      field: "name",
      flex: 1,
      renderCell: ({ row }: any) => (
        <>
          <Typography>{row?.name || row?.businessName || "N/A"}</Typography>
        </>
      )
    },
    {
      headerName: "Type",
      field: "type",
      flex: 1,
      renderCell: (params: any) => {
        const { type, isSendMessage, isSendEmail } = params.row;

        const isSuccessful =
          type === "EMAIL" ? isSendEmail :
            type === "WHATSAPP" ? isSendMessage :
              false;

        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography>{type}</Typography>
            <Tooltip title={isSuccessful ? "Sent" : "Not Sent"}>
              {isSuccessful ? (
                <CheckCircleIcon style={{ color: "green" }} />
              ) : (
                <CancelIcon style={{ color: "red" }} />
              )}
            </Tooltip>
          </Box>
        );
      },
    },
    {
      headerName: "Message Text",
      field: "messageText",
      flex: 1,
      renderCell: ({ row }: any) => (
        <Typography variant="body2">
          {row?.messageText || "-"}
        </Typography>
      )
    },
  ];


  // const { loading, error, data, refetch } = useQuery(GET_USER_LIST_MASSAGES);
  const { loading, error, data, refetch } = useQuery(GET_ALL_NOTIFICATIONS, {
    variables: {
      page: pagination.page,
      limit: pagination.pageSize,
      type: selectedTypes,
    },
  });
  
  const isEmailSelected = selectedTypes.includes("EMAIL");
  const isWhatsAppSelected = selectedTypes.includes("WHATSAPP");

  const queryVariables: any = {
    page: pagination.page,
    limit: pagination.pageSize,
    search: searchTerm,
    type: selectedTypes,
  };

  if (isEmailSelected) queryVariables.isSendEmail = isSendEmail;
  if (isWhatsAppSelected) queryVariables.isSendMessage = isSendMessage;

  refetch(queryVariables);

  useEffect(() => {
    if (data?.getSendBulkNotifications?.data) {
      const updatedList = data.getSendBulkNotifications.data.map((item: any) => ({
        ...item,
        key: item._id, // use this for stable identity
      }));
      setList(updatedList);
      setTotal(data.getSendBulkNotifications.count || 0);
    }
  }, [data]);

  const handleRowSelect = (index: number) => {
    const selectedItem = list.find((item: any) => item.index === index);
    if (!selectedItem) return;

    setSelectedIndexes((prevSelected) => {
      const isSelected = prevSelected.includes(index);
      const newIndexes = isSelected
        ? prevSelected.filter((i) => i !== index)
        : [...prevSelected, index];

      // Update selectedRows at the same time
      setSelectedRows((prevRows) => {
        const exists = prevRows.some((row: any) => row.index === index);
        return exists
          ? prevRows.filter((row: any) => row.index !== index)
          : [...prevRows, selectedItem];
      });

      return newIndexes;
    });
  };


  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      const allIndexes = list.map((item: any) => item.index);
      setSelectedIndexes(allIndexes);
      setSelectedRows(list); // this sets the new list
    } else {
      setSelectedIndexes([]);
      setSelectedRows([]);
    }
  };

  useEffect(() => {
    const validIndexes = list.map((item: any) => item.index);
    setSelectedRows((prev) =>
      prev.filter((row: any) => validIndexes.includes(row.index))
    );
    setSelectedIndexes((prev) =>
      prev.filter((idx) => validIndexes.includes(idx))
    );
  }, [list]);

  const debouncedRefetch = useCallback(
    debounce((term: string, selectedType: string[]) => {
      setSearchTerm(term.length > 0 ? term : "");
      refetch({
        queryVariables
      });
    }, 1000),
    [refetch, pagination.page, pagination.pageSize]
  );


  const handleCloseModal = () => {
    setSendMessagesObj(initialSendMessagesObj);
    setOpenModal(false);
  };

  const handleClose = () => {
    navigate(ROUTES.SEND_BULK_MESSAGES);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
    debouncedRefetch(value, selectedTypes);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setPagination((prev) => ({ ...prev, page: 1 }));
    refetch({ search: "", page: 1, limit: pagination.pageSize });
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
    refetch({
      search: searchTerm,
      page: newPage + 1,
      limit: pagination.pageSize,
    });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination((prev) => ({ ...prev, pageSize: newPageSize }));
    refetch({
      search: searchTerm,
      page: pagination.page,
      limit: newPageSize,
    });
  };

  const handleTypeChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    setSelectedTypes(typeof value === "string" ? value.split(",") : value);
    refetch({
      userType: typeof value === "string" ? value.split(",") : value,
      businessClaimType,
    });
  };

  const handleOpenModal = () => {
    setSendMessagesObj({
      ...initialSendMessagesObj,
      userList: selectedRows, // Pass the selected rows to the modal
    });
    setOpenModal(true);
  };


  const typeLabels: Record<string, string> = {
    EMAIL: "Email",
    WHATSAPP: "WhatsApp",
  };

  if (loading) return <Loader />;
  if (error) return <ErrorComponent />;

  return (
    <>
      <Box p={2}>
        {!openModal && (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              paddingRight={4}
            >
              <Box style={{ display: "flex", alignItems: "center" }}>
                <Tooltip title="Back">
                  <img
                    src={backButton}
                    alt="backButton"
                    height={25}
                    width={25}
                    style={{ marginRight: "12px", cursor: "pointer" }}
                    onClick={handleClose}
                  />
                </Tooltip>
                <Typography
                  variant="h4"
                  gutterBottom
                  style={{ color: "#00C5B9" }}
                  marginTop={2}
                >
                  Show Notifications
                </Typography>
              </Box>
              <Box display="flex" gap={4} mt={2} alignItems="center">
                {/* Email */}
                <Box>
                  {isEmailSelected && (
                    <Box display="flex" gap={2} ml={3}>
                      <FormControlLabel
                        control={
                          <Radio
                            checked={isSendEmail === true}
                            onChange={() => setIsSendEmail(true)}
                          />
                        }
                        label="Send"
                      />
                      <FormControlLabel
                        control={
                          <Radio
                            checked={isSendEmail === false}
                            onChange={() => setIsSendEmail(false)}
                          />
                        }
                        label="Do Not Send"
                      />
                    </Box>
                  )}
                </Box>

                {/* Message */}
                <Box>
                  {isWhatsAppSelected && (
                    <Box display="flex" gap={2} ml={3}>
                      <FormControlLabel
                        control={
                          <Radio
                            checked={isSendMessage === true}
                            onChange={() => setIsSendMessage(true)}
                          />
                        }
                        label="Send"
                      />
                      <FormControlLabel
                        control={
                          <Radio
                            checked={isSendMessage === false}
                            onChange={() => setIsSendMessage(false)}
                          />
                        }
                        label="Do Not Send"
                      />
                    </Box>
                  )}
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={2}>
                <Box width={350}>
                  <Select
                    displayEmpty
                    value={selectedTypes}
                    onChange={handleTypeChange}
                    size="small"
                    fullWidth
                    renderValue={(selected) =>
                      selected.length === 0
                        ? "Select Types"
                        : (selected as string[]).map((val) => typeLabels[val])
                    }
                  >
                    {Object.keys(typeLabels).map((type) => (
                      <MenuItem key={type} value={type}>
                        <Checkbox checked={selectedTypes.indexOf(type) > -1} />
                        <Typography variant="body2">{typeLabels[type]}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box width={200}>
                  <TextField
                    label="Search"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton onClick={handleClearSearch}>
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>
            </Box>
            <Typography variant="body2" color="textSecondary">
              {selectedRows.length > pagination.pageSize &&
                `You have selected ${selectedRows.length} items across all pages.`}
            </Typography>
            <Typography variant="h6">Selected Users ({selectedRows?.length})</Typography>
            <Box mt={2}>
              <CustomTable
                columns={COLUMNS}
                data={list}
                paginationModel={{
                  page: pagination.page - 1,
                  pageSize: pagination.pageSize,
                }}
                totalCount={total}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              // hidePagination={true}
              />
            </Box>
          </>
        )}
        {openModal && (
          <SendBulkMessagesModal
            handleClose={handleCloseModal}
            refetchSendMessages={() => refetch()}
            initialSendMessagesObj={initialSendMessagesObj}
            sendMessagesObj={sendMessagesObj}
            setSendMessagesObj={setSendMessagesObj}
          />
        )}
      </Box>
    </>
  );
}

export default ShowNotifications;